import { Injectable } from '@angular/core';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { TokenService } from './token-service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthGuardService implements CanActivate {
  constructor(
    private router: Router,
    private tokenService: TokenService,
  ) {}
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    // 取得完整網址 (包含後面的 JWT Token)
    const fullUrlWithToken = window.location.href;

    // 存入 sessionStorage (關閉分頁前都會在)
    sessionStorage.setItem('debug_last_full_url', fullUrlWithToken);

    // 用顏色最鮮明顯眼的方式印出來，防止被其他 log 淹沒
    // console.log(
    //   '%c 🎯 偵測到原始 Token 網址:',
    //   'background: #ff0000; color: #fff; font-size: 14px; padding: 4px;',
    //   fullUrlWithToken,
    // );

    // console.log('[AuthGuard] 🔍 canActivate 檢查開始');
    // console.log('[AuthGuard] route.url:', route.url);
    // console.log('[AuthGuard] route.queryParams:', route.queryParams);
    // console.log('[AuthGuard] window.location.href:', window.location.href);
    // console.log('[AuthGuard] window.location.hash:', window.location.hash);

    // 額外檢查：直接從 window.location.hash 解析 token，以防 Angular 路由沒有正確解析
    const hash = window.location.hash;
    let urlToken: string | null = null;
    if (hash.includes('?token=')) {
      try {
        const hashParts = hash.split('?');
        const queryString = hashParts[1];
        if (queryString) {
          const params = new URLSearchParams(queryString);
          urlToken = params.get('token');
          console.log('[AuthGuard] 從 hash 直接解析 token:', urlToken ? '✅' : '❌');
        }
      } catch (error) {
        console.error('[AuthGuard] 解析 hash 中的 token 時出錯:', error);
      }
    }

    // 如果沒有從 hash 找到，嘗試標準方式
    if (!urlToken) {
      const tokenFromQuery = route.queryParams['token'];
      const tokenFromHash = this.extractTokenFromHash();
      urlToken = tokenFromQuery || tokenFromHash;
    }

    // console.log('[AuthGuard] tokenFromQuery:', route.queryParams['token'] ? '✅' : '❌');
    // console.log('[AuthGuard] tokenFromHash:', this.extractTokenFromHash() ? '✅' : '❌');
    // console.log('[AuthGuard] URL 中的 token =', urlToken ? '✅ 已取得' : '❌ 為 null');

    // 核心修改：即使已經登入，如果 URL 中有新 token，也要檢查是否需要切換用戶
    if (urlToken) {
      const currentToken = this.tokenService.getLoginToken();
      const tokenChanged = currentToken !== urlToken;

      // console.log('[AuthGuard] 現有 token:', currentToken ? '✅ 存在' : '❌ 不存在');
      // console.log('[AuthGuard] Token 是否改變:', tokenChanged ? '✅ 改變' : '❌ 相同');

      if (tokenChanged) {
        console.log('[AuthGuard] ✅ 檢測到新的 Token，正在重新登入...');
        this.tokenService.setLogonToken(urlToken);
      } else {
        console.log('[AuthGuard] Token 相同，無需重新登入');
      }

      // 清理 URL 中的 token 參數
      // 原始網址可能是：http://localhost:4200/#/forms-flow?token=xxx
      // 目標網址：http://localhost:4200/#/forms-flow
      let cleanUrl = window.location.href;

      // 先移除 hash 部分進行處理
      const hashIndex = cleanUrl.indexOf('#');
      let hashPart = '';
      let baseUrl = cleanUrl;

      if (hashIndex !== -1) {
        baseUrl = cleanUrl.substring(0, hashIndex);
        hashPart = cleanUrl.substring(hashIndex);
      }

      // 移除 baseUrl 中的 token 參數
      baseUrl = baseUrl
        .replace(/[?&]token=[^&]*/, '')
        .replace(/\?$/, '')
        .replace(/&token=[^&]*/, '');

      // 移除 hash 部分中的 token 參數
      if (hashPart.includes('?')) {
        const hashPathEnd = hashPart.indexOf('?');
        const hashPath = hashPart.substring(0, hashPathEnd);
        const hashQuery = hashPart.substring(hashPathEnd + 1);

        // 解析並移除 token 參數
        const params = new URLSearchParams(hashQuery);
        params.delete('token');

        const newHashQuery = params.toString();
        hashPart = newHashQuery ? hashPath + '?' + newHashQuery : hashPath;
      }

      cleanUrl = baseUrl + hashPart;
      window.history.replaceState({}, '', cleanUrl);
      console.log('[AuthGuard] 🔧 已清理 URL 中的 Token 參數:', cleanUrl);

      return true;
    }

    // 2. 如果沒有新的 Token，檢查現有登入狀態
    const userId = this.tokenService.getLoginStaNum();
    console.log('[AuthGuard] 檢查現有登入狀態，userId:', userId);
    if (userId) {
      console.log('[AuthGuard] 🔄 目前登入者 Id:', userId);
      return true;
    }

    console.warn('[AuthGuard] ❌ 無效的 Token 且未登入，重定向到登入頁');
    window.location.href = environment.loginUrl;
    return false;
  }

  /**
   * 輔助方法：從 Hash 中解析 Token (#/?token=...)
   */
  private extractTokenFromHash(): string | null {
    const hash = window.location.hash;
    // console.log('[AuthGuard] extractTokenFromHash()：hash =', hash);

    if (hash.includes('token=')) {
      try {
        const hashParts = hash.split('?');
        console.log('[AuthGuard] hash.split("?") =', hashParts);

        const queryString = hashParts[1];
        if (!queryString) {
          console.warn('[AuthGuard] queryString 為空');
          return null;
        }

        const params = new URLSearchParams(queryString);
        const extractedToken = params.get('token');
        console.log(
          '[AuthGuard] URLSearchParams.get("token") =',
          extractedToken ? '✅ 取得' : '❌ null',
        );

        return extractedToken;
      } catch (error) {
        console.error('[AuthGuard] extractTokenFromHash() 發生錯誤:', error);
        return null;
      }
    }

    // console.log('[AuthGuard] hash 中不包含 "token="');
    return null;
  }
}
